class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    this.strings = this.readI18n();
    this.onSubmit = this.onSubmit.bind(this);
    this.onClick = this.onClick.bind(this);
    this.addEventListener('submit', this.onSubmit);
    this.addEventListener('click', this.onClick);
    this.CART_UPDATE = '/cart/update.js';
    this.CART_JSON   = '/cart.js';
    this.DRAWER_SECTION_URL = `${(window.routes?.cart_url)||'/cart'}?section_id=cart-drawer`;
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }

    async refreshDrawer() {
    const html = await (await fetch(this.DRAWER_SECTION_URL, { credentials:'same-origin' })).text();
    const doc = new DOMParser().parseFromString(html, 'text/html');

    const srcInner = doc.querySelector('#CartDrawer .drawer__inner');
    const dstInner = document.querySelector('#CartDrawer .drawer__inner');
    if (srcInner && dstInner) dstInner.innerHTML = srcInner.innerHTML;

    const srcBubble = doc.querySelector('#cart-icon-bubble');
    const dstBubble = document.querySelector('#cart-icon-bubble');
    if (srcBubble && dstBubble) dstBubble.outerHTML = srcBubble.outerHTML;

    this.classList.add('animate','active');
    this.querySelector('#CartDrawer-Overlay')?.addEventListener('click', this.close.bind(this));
    await this.hydrateDiscountInput();
    document.dispatchEvent(new CustomEvent('cart:drawer:refreshed'));
  }

  // ---------- Discount helpers ----------
  async applyDiscountRaw(code){
    return (await fetch(this.CART_UPDATE, {
      method:'POST',
      headers:{ 'Content-Type':'application/json' },
      credentials:'same-origin',
      body: JSON.stringify({ discount: code })
    })).json();
  }

  async clearDiscountsRaw(){
    return (await fetch(this.CART_UPDATE, {
      method:'POST',
      headers:{ 'Content-Type':'application/json' },
      credentials:'same-origin',
      body: JSON.stringify({ discount: '' })
    })).json();
  }

  async getCart(){
    return (await fetch(this.CART_JSON, { credentials:'same-origin' })).json();
  }

  wasAppliedByApplicableFlag(cart, rawCode){
    const codes = cart.discount_codes || [];
    const norm = (rawCode||'').trim().toLowerCase();
    const entry = codes.find(c => (c.code||'').toLowerCase() === norm);
    if (entry && typeof entry.applicable === 'boolean') return entry.applicable === true;
    const titles = (cart.cart_level_discount_applications||[]).map(a => (a.title||'').toLowerCase());
    return titles.includes(norm);
  }

  async hydrateDiscountInput(){
    const input = this.querySelector('#CartDrawer-DiscountInput');
    const clearBtn = this.querySelector('#CartDrawer-DiscountClear');
    if (!input) return;
    try {
      const cart = await this.getCart();
      const codes = (cart.discount_codes||[]).filter(d => d.applicable !== false).map(d => d.code);
      if (codes.length){
        input.value = codes.join(', ');
        clearBtn?.classList.remove('hidden');
      } else if (!(input.value || '').trim()){
        clearBtn?.classList.add('hidden');
      }
    } catch(_) {}
  }


  readI18n(){
    try {
      const el = this.querySelector('#CartDrawer-i18n') || document.getElementById('CartDrawer-i18n');
      return el ? JSON.parse(el.textContent) : {};
    } catch(_) { return {}; }
  }

  t(key, fallback=''){
    return (this.strings && this.strings[key]) || fallback;
  }

  // ---------- Event handlers (delegated) ----------
  async onSubmit(e){
    const form = e.target.closest('#CartDrawer-DiscountForm');
    if (!form) return;
    e.preventDefault();

    const input = form.querySelector('#CartDrawer-DiscountInput');
    const msg   = form.querySelector('#CartDrawer-DiscountMessage');
    const code  = (input?.value || '').trim();

    if (!code){ msg && (this.setDiscountMessage('error', this.t('enterCode', 'Enter a code.'))); input?.focus(); return; }
    msg && (this.setDiscountMessage(null, this.t('applying','Applying…')));

    try {
      const afterUpdate = await this.applyDiscountRaw(code);
      let ok = this.wasAppliedByApplicableFlag(afterUpdate, code);
      if (!ok){ const confirmCart = await this.getCart(); ok = this.wasAppliedByApplicableFlag(confirmCart, code); }

      await this.refreshDrawer();

      const newInput = this.querySelector('#CartDrawer-DiscountInput');
      const newMsg   = this.querySelector('#CartDrawer-DiscountMessage');
      const newClear = this.querySelector('#CartDrawer-DiscountClear');

      if (newInput) newInput.value = code;
      if (ok){
        newClear?.classList.remove('hidden');
        this.setDiscountMessage('success', this.t('applied', 'Discount applied.'));
      } else {
        newClear?.classList.add('hidden');
        this.setDiscountMessage('error', this.t('couldntApply', 'Couldn’t apply this code.'));
      }

      try { window.publish && window.PUB_SUB_EVENTS && window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: ok ? 'discount-apply:ok' : 'discount-apply:fail' }); } catch(_){}
    } catch(err){
      msg && (this.setDiscountMessage('error', this.t('couldntApply', 'Couldn’t apply this code.')));
      console.error(err);
    }
  }

  async onClick(e){
    const btn = e.target.closest('#CartDrawer-DiscountClear');
    if (!btn) return;

    const form = btn.closest('#CartDrawer-DiscountForm');
    const msg  = form?.querySelector('#CartDrawer-DiscountMessage');

    btn.disabled = true;
    msg && (this.setDiscountMessage(null, this.t('removing', 'Removing…')));

    try {
      await this.clearDiscountsRaw();
      const confirmCart = await this.getCart();
      const stillAny = (confirmCart.cart_level_discount_applications||[]).length > 0 || (confirmCart.discount_codes||[]).length > 0;

      await this.refreshDrawer();

      const newInput = this.querySelector('#CartDrawer-DiscountInput');
      const newMsg   = this.querySelector('#CartDrawer-DiscountMessage');
      const newClear = this.querySelector('#CartDrawer-DiscountClear');

      if (!stillAny){
        newInput && (newInput.value = '');
        newClear && newClear.classList.add('hidden');
        newMsg && (this.setDiscountMessage(null, this.t('removed', 'Discount removed.')));
      } else {
        newMsg && (this.setDiscountMessage('error', this.t('couldntRemove', 'Couldn’t remove discount.')));
      }

      try { window.publish && window.PUB_SUB_EVENTS && window.publish(window.PUB_SUB_EVENTS.cartUpdate, { source: !stillAny ? 'discount-remove:ok' : 'discount-remove:fail' }); } catch(_){}
    } catch(err){
      msg && (this.setDiscountMessage('error', this.t('couldntRemove', 'Couldn’t remove discount.')));
      console.error(err);
    } finally {
      btn.disabled = false;
    }
  }
  setDiscountMessage(type, text){
    const msg = this.querySelector('#CartDrawer-DiscountMessage');
    if (!msg) return;
    msg.textContent = text || '';
    msg.classList.remove('is-error','is-success');
    if (type === 'error')  msg.classList.add('is-error');
    if (type === 'success') msg.classList.add('is-success');
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

document.addEventListener('DOMContentLoaded', () => {
  const params = new URLSearchParams(window.location.search);
  if (!params.has('open_cart')) return;
  const cartDrawer = document.querySelector('cart-drawer');
  if (cartDrawer && typeof cartDrawer.open === 'function') {
    cartDrawer.open();
  }
  params.delete('open_cart');
  const newSearch = params.toString();
  const newUrl = window.location.pathname + (newSearch ? `?${newSearch}` : '') + window.location.hash;
  window.history.replaceState({}, '', newUrl);
});

document.addEventListener('click', (event) => {
  if (event.defaultPrevented) return;
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const link = event.target.closest('a[href]');
  if (!link) return;
  const href = link.getAttribute('href');
  if (!href) return;

  let url;
  try {
    url = new URL(href, window.location.origin);
  } catch {
    return;
  }
  if (url.origin !== window.location.origin) return;

  const cartPath = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
    ? `${window.Shopify.routes.root.replace(/\/$/, '')}/cart`
    : '/cart';
  const path = url.pathname.replace(/\/$/, '') || '/';
  if (path !== cartPath && path !== `${cartPath}/`) return;
  if (url.search || url.hash) return;

  const cartDrawer = document.querySelector('cart-drawer');
  if (!cartDrawer || typeof cartDrawer.open !== 'function') return;
  event.preventDefault();
  cartDrawer.open(link);
});
